import React, { useContext, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import Figure from 'react-bootstrap/Figure';
import PhotoGallery from '../components/PhotoGallery';
import { AppContext } from '../context/AppContext';
import { useParams } from 'react-router-dom';
import { SCNavbar } from '../components/Navbar';
import { Helmet } from 'react-helmet';
import { ItemType } from '../models/models';

const DetailPage: React.FC = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("AppContext must be used within an AppProvider");
  }

  const { state, updateScientificCommittee } = context;
  const { scientificCommittee, type, id } = useParams<{ scientificCommittee: string; type: string; id: string }>();
  const { data } = state;
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [canonical, setCanonical] = useState<string>('');

  useEffect(() => {
    if (scientificCommittee) {
      updateScientificCommittee(scientificCommittee);
    }
  }, [scientificCommittee, updateScientificCommittee]);

  useEffect(() => {
    if (!data || !type || !id) return;
    const item = data[type]?.find((item: any) => item.id === parseInt(id));
    if (item) {
      setSelectedItem(item);
      setCanonical(getCanonical());
    }
  }, [data, id, type]);

  const formatDate = (date: string): string => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getCanonical = (): string => {
    return `${process.env.REACT_APP_BASE_PATH}/${scientificCommittee}/${type}/${type}`;
  }

  const renderFigure = (url?: string, caption?: string) => {
    if (!url) return null;
    return (
      <Figure className="w-100 my-4">
        <Figure.Image
          alt={caption || 'Detail image'}
          src={url}
          style={{ objectFit: 'contain', objectPosition: 'center', width: '100%', height: '350px' }}
        />
        <Figure.Caption>{caption}</Figure.Caption>
      </Figure>
    );
  };

  function capitalize(text: string): string {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  if (!selectedItem) {
    return (
      <div className="container py-4 flex-grow-1 d-flex align-items-center justify-content-center flex-column">
        <h1>Page Not Found</h1>
        <p> The requested page does not exist or is not available.</p>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{selectedItem.title} | ICOH - International Commission on Occupational Health</title>
        <meta name="description" content={selectedItem.intro || selectedItem.period + ' - ' + selectedItem.place} />
        <link rel="canonical" href={canonical} />
      </Helmet>
      <SCNavbar backToScientificCommitee={scientificCommittee} section={selectedItem.type} scientificCommittee={scientificCommittee} />
      <div className="container py-4 flex-grow-1">
        <div className="d-flex align-items-center pb-2">
          <strong className="text-primary me-2">{capitalize(selectedItem.type)}</strong>
          {selectedItem.date && <em>| Published on {formatDate(selectedItem.date)}</em>}
        </div>
        {(selectedItem.period || selectedItem.place) && <div className="h4 mb-2 text-tertiary">
          {selectedItem.period} 
          {selectedItem.period && selectedItem.place && <span className="mx-2">-</span>}
          {selectedItem.place}
        </div>}
        <h1>{selectedItem.title}</h1>
        <div className="h4 text-tertiary my-2 lh-base">{selectedItem.intro}</div>
        {selectedItem.imageOriginal && selectedItem.type !== ItemType.GALLERY && renderFigure(selectedItem.imageOriginal, selectedItem.imageOriginalAlt)}
        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedItem.text) }}></div>
        {selectedItem.attachments?.length > 0 && (
          <div className="py-4">
              {selectedItem.attachments.map((attachment: any, index: number) => (
                <button key={index} className="btn btn-primary" onClick={() => window.open(attachment.link, '_blank')}>
                  {attachment.name}
                </button>
              ))}
          </div>
        )}
        {selectedItem.images?.length > 0 && (
          <div className="py-2">
            <PhotoGallery images={selectedItem.images} />
          </div>
        )}
      </div>
    </>
  );
};

export default DetailPage;
